// @flow

import React from 'react';
import StoreDetails from './chart/StoreDetails';
import type {
  AverageChartComparables,
  BranchLevelGemini,
  ShopHistory,
  ShoppingCenterGemini,
  StoreWithProxyStores,
  YearMonth,
} from '../../../../types/geminiType';
import geminiApi from '../../../../network/api/geminiApi';
import EllipsisLoader from '../../../../components/EllipsisLoader';
import NoElementFound from '../../../../components/NoElementFound';
import { getCurrentPcId } from '../../../../utils/urlUtils';
import computeAvg from '../../../../utils/computeAvg';
import {
  erv,
  glaWithoutSst,
  glaWithSst,
  invSbr,
  occupancyCost,
  ocr,
  revPot,
  salesNum,
  salesR12m,
  spotMgr,
} from './geminiConst';
import { notifyApolloError } from '../../../../network/notification';

type Props = {
  dispatch: () => void,
  codstr: string,
  retailerId: string,
  shoppingCenterData: ShoppingCenterGemini,
  branchKpiAvg: BranchLevelGemini[],
  yearMonth: YearMonth,
}

type State = {
  shopHistory: ?ShopHistory,
  isShopHistoryLoading: boolean,
  storeWithProxyStores: StoreWithProxyStores,
  isProxyStoresLoading: boolean,
  averageComparables: AverageChartComparables,
  isAverageComparableLoading: boolean,
}

class ChartModal extends React.Component<Props, State> {
  state = {
    shopHistory: null,
    isShopHistoryLoading: true,
    storeWithProxyStores: {},
    isProxyStoresLoading: true,
    averageComparables: {},
    isAverageComparableLoading: false,
  };

  fetchProxyStores = () => {
    this.setState({ isProxyStoresLoading: true });
    geminiApi.fetchStoreProxyStoresSale(this.props.codstr)
      .then(response => response.json())
      .then((storeWithProxyStores: StoreWithProxyStores) => this.setState({ storeWithProxyStores }))
      .catch(e => this.props.dispatch(notifyApolloError(e)))
      .finally(() => this.setState({ isProxyStoresLoading: false }));
  }

  fetchStoreHistory = () => {
    this.setState({ isShopHistoryLoading: true });
    geminiApi.fetchStoresHistory(getCurrentPcId(), this.props.codstr)
      .then(res => res.json())
      .then((shopHistory) => {
        this.setState({ shopHistory });
      })
      .catch(() => this.setState({ shopHistory: null }))
      .finally(() => this.setState({ isShopHistoryLoading: false }));
  };

  computeAvgComparables = () => {
    this.setState({ isAverageComparableLoading: true });
    geminiApi.fetchStoresComparable(this.props.codstr, this.props.retailerId, getCurrentPcId())
      .then(res => res.json())
      .then((json) => {
        this.setState({
          averageComparables: {
            salesR12m: computeAvg(json.storeComparables, salesR12m),
            salesR12mWithoutSst: computeAvg(json.storeComparables, salesR12m, null, true),
            glaWithSst: computeAvg(json.storeComparables, glaWithSst),
            glaWithoutSst: computeAvg(json.storeComparables, glaWithoutSst),
            ocr: computeAvg(json.storeComparables, ocr),
            salesNum: computeAvg(json.storeComparables, salesNum),
            erv: computeAvg(json.storeComparables, erv),
            spotMgr: computeAvg(json.storeComparables, spotMgr),
            revPot: computeAvg(json.storeComparables, revPot),
            invSbr: computeAvg(json.storeComparables, invSbr),
            occupancyCost: computeAvg(json.storeComparables, occupancyCost),
          },
        });
      })
      .catch(() => this.setState({ averageComparables: null }))
      .finally(() => this.setState({ isAverageComparableLoading: false }));
  };

  getComponent = () => (
    this.state.shopHistory
      ? (
        <StoreDetails
          branchKpiAvg={this.props.branchKpiAvg}
          shoppingCenterData={this.props.shoppingCenterData}
          shopHistory={this.state.shopHistory}
          averageComparables={this.state.averageComparables}
          proxyStores={this.state.storeWithProxyStores.proxyStores}
        />
      ) : (
        <NoElementFound text="No Data" />
      )
  );

  componentDidMount() {
    this.fetchProxyStores();
    this.fetchStoreHistory();
    this.computeAvgComparables();
  }

  render() {
    return (
      (!this.state.isShopHistoryLoading && !this.state.isProxyStoresLoading && !this.state.isAverageComparableLoading)
        ? this.getComponent() : <EllipsisLoader />
    );
  }
}

export default ChartModal;
